import { Mail, Phone, MapPin, Linkedin, Globe, Github, Calendar, Award } from 'lucide-react'
import { getColorClasses } from '@/lib/templateColors'

interface ResumeTemplateProps {
  data: any
  language?: string
  labels: any
  color?: string
}


export default function CoralPinkTemplate({ data, labels, color = 'blue' }: ResumeTemplateProps) {
  const colors = getColorClasses(color)
  const { personalInfo, summary, experience, education, skills, projects, certifications } = data

  return (

      <div className="bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden">
        <div id="resume-preview" className="bg-white" style={{ width: '8.5in', minHeight: '11in' }}>
          {/* Coral Header */}
          <div className={`bg-gradient-to-br ${colors.headerBg} ${colors.headerText} p-10`}>
            <h1 className="text-5xl font-bold mb-3">{personalInfo.fullName || 'YOUR NAME'}</h1>
            <div className="flex flex-wrap gap-6 text-sm">
              {personalInfo.email && <div className="flex items-center space-x-2"><Mail className="w-4 h-4" /><span>{personalInfo.email}</span></div>}
              {personalInfo.phone && <div className="flex items-center space-x-2"><Phone className="w-4 h-4" /><span>{personalInfo.phone}</span></div>}
              {personalInfo.address && <div className="flex items-center space-x-2"><MapPin className="w-4 h-4" /><span>{personalInfo.address}</span></div>}
            </div>
          </div>

          <div className="p-8">
            {summary && (
              <div className="mb-6">
                <div className="flex items-center mb-3">
                  <div className={`h-8 w-2 bg-gradient-to-b ${colors.accentBorder} mr-3`}></div>
                  <h2 className={`text-2xl font-bold ${colors.sectionTitle}`}>{labels.professionalSummary}</h2>
                </div>
                <p className="text-gray-700 leading-relaxed pl-5">{summary}</p>
              </div>
            )}

            {experience && experience.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center mb-4">
                  <div className={`h-8 w-2 bg-gradient-to-b ${colors.accentBorder} mr-3`}></div>
                  <h2 className={`text-2xl font-bold ${colors.sectionTitle}`}>{labels.workExperience}</h2>
                </div>
                <div className="space-y-5 pl-5">
                  {experience.map((exp: any) => (
                    <div key={exp.id} className={`border-l-4 ${colors.timelineBorder} pl-4`}>
                      <h3 className="text-lg font-bold text-gray-900">{exp.jobTitle}</h3>
                      <div className={`${colors.accentText} font-semibold`}>{exp.company}</div>
                      <div className="text-sm text-gray-600 mb-2">{exp.startDate} - {exp.endDate || labels.present}</div>
                      <div className="text-gray-700 whitespace-pre-line text-sm">{exp.description}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {skills && skills.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center mb-4">
                  <div className={`h-8 w-2 bg-gradient-to-b ${colors.accentBorder} mr-3`}></div>
                  <h2 className={`text-2xl font-bold ${colors.sectionTitle}`}>{labels.skills}</h2>
                </div>
                <div className="grid grid-cols-2 gap-4 pl-5">
                  {skills.map((skillGroup: any, idx: number) => (
                    <div key={idx} className={`bg-gradient-to-br ${colors.badgeBg} p-4 rounded-lg`}>
                      <div className={`font-bold ${colors.accentText} mb-2`}>{skillGroup.category}</div>
                      <div className="space-y-1">
                        {skillGroup.skills?.map((skill: string, skillIdx: number) => (
                          <div key={skillIdx} className="text-sm text-gray-700">• {skill}</div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {education && education.length > 0 && (
              <div>
                <div className="flex items-center mb-4">
                  <div className={`h-8 w-2 bg-gradient-to-b ${colors.accentBorder} mr-3`}></div>
                  <h2 className={`text-2xl font-bold ${colors.sectionTitle}`}>{labels.education}</h2>
                </div>
                <div className="space-y-3 pl-5">
                  {education.map((edu: any) => (
                    <div key={edu.id}>
                      <div className="font-bold text-gray-900">{edu.degree}</div>
                      <div className={colors.accentText}>{edu.institution}</div>
                      <div className="text-sm text-gray-600">{edu.graduationDate}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
  )
}
